import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { ScrollText, Building2, Save, Loader2, RotateCcw } from 'lucide-react';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const Field = ({ label, value, onChange, testId, placeholder }) => (
  <div>
    <label className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-bold block mb-2" style={FONT_B}>{label}</label>
    <input
      value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-white/30"
      data-testid={testId}
    />
  </div>
);

export const TemplatesView = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('contract');
  const [agreementText, setAgreementText] = useState('');
  const [biz, setBiz] = useState({ name: '', tagline: '', location: '' });
  const [dirty, setDirty] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get('/api/admin/templates');
      setAgreementText(d.agreement_text || '');
      setBiz(d.business || { name: '', tagline: '', location: '' });
      setUpdatedAt(d.updated_at);
      setDirty(false);
    } catch (e) { toast.error('Failed to load templates'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const d = await api.put('/api/admin/templates', {
        agreement_text: agreementText,
        business_name: biz.name,
        business_tagline: biz.tagline,
        business_location: biz.location,
      });
      setUpdatedAt(d.updated_at);
      setDirty(false);
      toast.success('Templates saved');
    } catch (e) { toast.error(e.message || 'Save failed'); }
    setBusy(false);
  };

  const reset = async () => {
    if (!window.confirm('Reset to factory defaults? Your custom edits will be lost.')) return;
    setBusy(true);
    try {
      const d = await api.post('/api/admin/templates/reset', {});
      setAgreementText(d.agreement_text || '');
      setBiz(d.business);
      setUpdatedAt(d.updated_at);
      setDirty(false);
      toast.success('Reset to defaults');
    } catch (e) { toast.error('Reset failed'); }
    setBusy(false);
  };

  if (loading) return <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading templates…</p>;

  return (
    <div className="space-y-8" data-testid="templates-view">
      <div>
        <h2 className="text-white text-[26px] font-black tracking-tighter leading-none" style={FONT_H}>Templates</h2>
        <p className="text-zinc-500 text-sm mt-2" style={FONT_B}>
          Edit what appears on client-facing invoices and contracts.
          {updatedAt && <span className="text-zinc-600"> · Last saved {new Date(updatedAt).toLocaleString()}</span>}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          ['contract', 'Contract Terms', ScrollText, 'text-cyan-300'],
          ['business', 'Business Info', Building2, 'text-violet-300'],
        ].map(([k, label, Icon, color]) => (
          <button
            key={k} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-bold transition-colors ${
              tab === k ? 'bg-white text-black' : 'bg-white/5 border border-white/10 text-zinc-400'
            }`}
            style={FONT_B} data-testid={`templates-tab-${k}`}
          >
            <Icon className={`w-3.5 h-3.5 ${tab === k ? 'text-black' : color}`} /> {label}
          </button>
        ))}
      </div>

      {tab === 'contract' && (
        <div className="space-y-3" data-testid="templates-contract-panel">
          <label className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-bold block" style={FONT_B}>Agreement Text</label>
          <p className="text-zinc-600 text-xs" style={FONT_B}>
            Shown as the Service Agreement on every client-facing invoice page. Clients type their name to e-sign this exact text.
          </p>
          <textarea
            value={agreementText}
            onChange={(e) => { setAgreementText(e.target.value); setDirty(true); }}
            rows={22}
            className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-4 text-[13px] text-zinc-200 leading-relaxed focus:outline-none focus:border-cyan-500/40 resize-none"
            style={FONT_B}
            data-testid="templates-agreement-input"
          />
          <p className="text-zinc-600 text-[11px] tabular-nums" style={FONT_B}>{agreementText.length} characters</p>
        </div>
      )}

      {tab === 'business' && (
        <div className="space-y-5" data-testid="templates-business-panel">
          <p className="text-zinc-600 text-xs" style={FONT_B}>
            Shown at the top of every client-facing invoice + agreement page.
          </p>
          <Field label="Business Name" value={biz.name}
                 onChange={(v) => { setBiz(b => ({ ...b, name: v })); setDirty(true); }}
                 testId="templates-biz-name" placeholder="Exotic Animals Miami" />
          <Field label="Tagline" value={biz.tagline}
                 onChange={(v) => { setBiz(b => ({ ...b, tagline: v })); setDirty(true); }}
                 testId="templates-biz-tagline" placeholder="Private Exotic Animal Experiences" />
          <Field label="Location" value={biz.location}
                 onChange={(v) => { setBiz(b => ({ ...b, location: v })); setDirty(true); }}
                 testId="templates-biz-location" placeholder="Miami, FL" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-2">
        <button onClick={save} disabled={busy || !dirty}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-40 transition-transform"
                style={FONT_B} data-testid="templates-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {dirty ? 'Save changes' : 'Saved'}
        </button>
        <button onClick={reset} disabled={busy}
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-sm font-bold active:scale-95 disabled:opacity-40 transition-transform"
                style={FONT_B} data-testid="templates-reset">
          <RotateCcw className="w-4 h-4" /> Reset to defaults
        </button>
      </div>
    </div>
  );
};
